// Нативный Google Sign-In для Capacitor (Android/iOS).
// В WebView нельзя использовать обычный GIS (Google блокирует OAuth в embedded WebView),
// поэтому для нативной сборки вызываем плагин @capgo/capacitor-social-login,
// который открывает системный аккаунт Google и возвращает idToken (JWT).
// Возвращаемый idToken совместим с эндпоинтом POST /api/auth/google (поле `credential`).

export function isCapacitorNative() {
  return !!(
    typeof window !== 'undefined' &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform()
  );
}

function getSocialLoginPlugin() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.SocialLogin || null;
}

let initPromise = null;

function ensureInitialized(webClientId) {
  if (initPromise) return initPromise;
  const plugin = getSocialLoginPlugin();
  if (!plugin) {
    return Promise.reject(
      new Error('SocialLogin plugin is not registered (native sync missing?)')
    );
  }
  initPromise = plugin
    .initialize({ google: { webClientId } })
    .catch((err) => {
      initPromise = null;
      throw err;
    });
  return initPromise;
}

export async function nativeGoogleSignIn(webClientId) {
  if (!isCapacitorNative()) {
    throw new Error('Not a native platform');
  }
  const clientId = (webClientId || '').trim();
  if (!clientId) {
    throw new Error('REACT_APP_GOOGLE_CLIENT_ID is not set in build env');
  }
  await ensureInitialized(clientId);
  const plugin = getSocialLoginPlugin();
  if (!plugin) throw new Error('SocialLogin plugin disappeared after init');

  // ВАЖНО: без `scopes` и без `forceRefreshToken`. Плагин на Android
  // использует Credential Manager и требует модификации MainActivity,
  // если передан scopes. Нам нужен только idToken (email/profile уже в JWT).
  const res = await plugin.login({
    provider: 'google',
    options: {},
  });

  // Разные версии плагина раскладывают ответ по-разному.
  const result = res?.result || res || {};
  const idToken =
    result.idToken ||
    result.idTokenString ||
    result?.authentication?.idToken ||
    null;
  if (!idToken) {
    throw new Error('Google did not return idToken');
  }
  return idToken;
}

export async function nativeGoogleSignOut() {
  if (!isCapacitorNative()) return;
  const plugin = getSocialLoginPlugin();
  if (!plugin) return;
  try {
    await plugin.logout({ provider: 'google' });
  } catch {
    /* no-op */
  }
  initPromise = null;
}
